/** Era start dates (first day of each era). */
const ERAS = [
  { name: "令和", letter: "R", start: "2019-05-01" },
  { name: "平成", letter: "H", start: "1989-01-08" },
  { name: "昭和", letter: "S", start: "1926-12-25" },
  { name: "大正", letter: "T", start: "1912-07-30" },
  { name: "明治", letter: "M", start: "1868-01-25" },
] as const;

const toHalfWidth = (value: string): string =>
  value
    .replace(/[０-９Ａ-Ｚａ-ｚ]/g, (c) =>
      String.fromCharCode(c.charCodeAt(0) - 0xfee0)
    )
    .replace(/[／－．ー−‐]/g, (c) =>
      c === "／" ? "/" : c === "．" ? "." : "-"
    )
    .replace(/\s+/g, "");

const pad = (value: number): string => String(value).padStart(2, "0");

const lastDayOfMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

const isValidDate = (year: number, month: number, day: number): boolean =>
  year >= 1000 &&
  year <= 9999 &&
  month >= 1 &&
  month <= 12 &&
  day >= 1 &&
  day <= lastDayOfMonth(year, month);

const eraYear = (era: string): number | null => {
  const found = ERAS.find(
    (e) => e.name === era || e.letter === era.toUpperCase()
  );
  return found ? Number(found.start.slice(0, 4)) - 1 : null;
};

/**
 * 西暦・和暦の日付の入力を "YYYY-MM-DD" にする。
 * 年だけ・年月だけの入力は、範囲の始まり（`edge="start"`）なら最初の日、終わりなら最後の日にする。
 * 例: "平成12年3月1日"・"H12.3.1"・"r6/1"・"2000/3/1"・"20000301"・"2000年"
 * @returns 読めないときは null
 */
export const parseJapaneseDate = (
  input: string,
  edge: "start" | "end" = "start"
): string | null => {
  const value = toHalfWidth(input.trim());
  if (value === "") return null;

  let year: number;
  let rest: string;
  let eraIndex = -1;
  const wareki =
    /^(令和|平成|昭和|大正|明治|[RHSTMrhstm])(元|\d{1,2})(?:[年.\-/](.*))?$/.exec(
      value
    );
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
  if (wareki) {
    eraIndex = ERAS.findIndex(
      (e) => e.name === wareki[1] || e.letter === wareki[1].toUpperCase()
    );
    if (wareki[2] !== "元" && Number(wareki[2]) === 0) return null;
    const base = eraYear(wareki[1]);
    if (base == null) return null;
    year = base + (wareki[2] === "元" ? 1 : Number(wareki[2]));
    rest = wareki[3] ?? "";
  } else if (compact) {
    year = Number(compact[1]);
    rest = `${compact[2]}-${compact[3]}`;
  } else {
    const seireki = /^(\d{4})(?:[年.\-/](.*))?$/.exec(value);
    if (!seireki) return null;
    year = Number(seireki[1]);
    rest = seireki[2] ?? "";
  }

  const monthDay = /^(?:(\d{1,2})(?:[月.\-/](?:(\d{1,2})日?)?)?)?$/.exec(rest);
  if (!monthDay) return null;
  const month =
    monthDay[1] != null ? Number(monthDay[1]) : edge === "start" ? 1 : 12;
  if (month < 1 || month > 12) return null;
  const day =
    monthDay[2] != null
      ? Number(monthDay[2])
      : edge === "start"
      ? 1
      : lastDayOfMonth(year, month);
  if (!isValidDate(year, month, day)) return null;
  const date = `${year}-${pad(month)}-${pad(day)}`;
  if (eraIndex < 0) return date;
  const start = ERAS[eraIndex].start;
  const nextStart = eraIndex > 0 ? ERAS[eraIndex - 1].start : null;
  const intervalStart = `${year}-${pad(monthDay[1] != null ? month : 1)}-${pad(
    monthDay[2] != null ? day : 1
  )}`;
  const endMonth = monthDay[1] != null ? month : 12;
  const intervalEnd = `${year}-${pad(endMonth)}-${pad(
    monthDay[2] != null ? day : lastDayOfMonth(year, endMonth)
  )}`;
  if (intervalEnd < start || (nextStart != null && intervalStart >= nextStart))
    return null;
  if (edge === "start" && date < start) return start;
  if (edge === "end" && nextStart != null && date >= nextStart) {
    const previous = new Date(`${nextStart}T00:00:00Z`);
    previous.setUTCDate(previous.getUTCDate() - 1);
    return previous.toISOString().slice(0, 10);
  }
  return date;
};

/** "YYYY-MM-DD" を "平成12年3月1日" にする。明治より前・読めない値は null */
export const formatJapaneseDate = (iso: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const era = ERAS.find((e) => iso >= e.start);
  if (!era) return null;
  const year = Number(match[1]) - Number(era.start.slice(0, 4)) + 1;
  return `${era.name}${year === 1 ? "元" : year}年${Number(match[2])}月${Number(
    match[3]
  )}日`;
};

/** "YYYY-MM-DD" を今日から years 年前の日にする（築年数の条件に使う） */
export const yearsBefore = (years: number, today: Date): string => {
  const year = today.getFullYear() - years;
  const month = today.getMonth() + 1;
  const day = Math.min(today.getDate(), lastDayOfMonth(year, month));
  return `${year}-${pad(month)}-${pad(day)}`;
};
